import { useIntersection } from '@theme/use-intersection'

const ProductRecommendationsConfig = {
  reloadAttributes: [
    'data-product-id',
    'data-section-id',
    'data-intent',
    'data-url',
    'data-recommendations-performed'
  ]
}

class ProductRecommendations extends HTMLElement {
  #unobserve = null

  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.target !== this || mutation.type !== 'attributes') {
        continue
      }

      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      ) {
        continue
      }

      this.#loadRecommendations()
      break
    }
  })

  #cachedRecommendations = {}

  #activeFetch = null

  connectedCallback() {
    const io = useIntersection({ rootMargin: '0px 0px 400px 0px' })
    this.#unobserve = io.observe(this, (entry) => {
      if (!entry.isIntersecting) {
        return
      }
      io.unobserve(this)
      this.#loadRecommendations()
    })
    this.#mutationObserver.observe(this, {
      attributeFilter: ProductRecommendationsConfig.reloadAttributes,
      attributes: true
    })
  }

  disconnectedCallback() {
    this.#unobserve?.()
    this.#unobserve = null
    this.#mutationObserver.disconnect()
  }

  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } =
      this.dataset
    const id = this.id

    if (!(productId && id)) {
      this.#handleError(
        new Error('Product ID and an ID attribute are required')
      )
      return
    }

    if (recommendationsPerformed === 'true') {
      return
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`))
          }
          return
        }

        const html = document.createElement('div')
        html.innerHTML = result.data || ''
        const recommendations = html.querySelector(
          `product-recommendations[id="${id}"]`
        )

        if (recommendations?.innerHTML?.trim().length) {
          this.dataset.recommendationsPerformed = 'true'
          this.innerHTML = recommendations.innerHTML
        } else {
          this.#handleError(new Error('No recommendations available'))
        }
      })
      .catch((error) => {
        if (error?.name === 'AbortError') {
          return
        }
        this.#handleError(error)
      })
  }

  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`

    const cachedResponse = this.#cachedRecommendations[url]
    if (cachedResponse) {
      return { data: cachedResponse, success: true }
    }

    this.#activeFetch?.abort()
    this.#activeFetch = new AbortController()

    try {
      const response = await fetch(url, {
        signal: this.#activeFetch.signal
      })
      if (!response.ok) {
        return { status: response.status, success: false }
      }

      const text = await response.text()
      this.#cachedRecommendations[url] = text
      return { data: text, success: true }
    } finally {
      this.#activeFetch = null
    }
  }

  #handleError() {
    this.classList.add('hidden')
    this.dataset.error = 'Error loading product recommendations'
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations)
}
